import React, { useEffect, useRef, useState } from "react";
import manifest from "../content/bustFrames.json";
import { validateColorGrade } from "./bustColor";
import { MAX_YAW, mountBustMotion } from "./bustMotion";
import { createPortraitLoader } from "./portraitLoader";
import { prepareTorsoTexture } from "./bustTorso";
import "../styles/bustSignal.css";

const ASSET_ROOT = (process.env.PUBLIC_URL || "") + "/media/bust-v7-webp-q80";
const ACTIVE_MANIFEST = { ...manifest, frames: manifest.frames.filter(frame => frame.yaw <= MAX_YAW) };
const REST_FRAME = manifest.frames.find(frame => frame.yaw === -15);
const FALLBACK_STYLE = {
  left: (REST_FRAME.sprite_transform.x / manifest.width * 100) + "%",
  top: (REST_FRAME.sprite_transform.y / manifest.height * 100) + "%",
  width: (REST_FRAME.sprite_transform.scale * 100) + "%",
  height: (REST_FRAME.sprite_transform.scale * 100) + "%",
};

function validateManifest() {
  if (manifest.width !== 512 || manifest.height !== 640 || manifest.frames.length !== 49 ||
      new Set(manifest.frames.map(f => f.yaw)).size !== manifest.frames.length ||
      !manifest.frames.some(f => f.yaw === -15) || !manifest.frames.some(f => f.yaw === 0)) {
    throw new Error("Incomplete portrait sequence");
  }
  manifest.frames.forEach((frame, i) => {
    const t = frame.sprite_transform;
    if (!Number.isFinite(frame.yaw) || frame.yaw < -51 || frame.yaw > 51 ||
        (i > 0 && frame.yaw <= manifest.frames[i - 1].yaw) ||
        !/^yaw-[mp]\d{3}\.\d{3}\.webp$/.test(frame.file) ||
        !t || !Number.isFinite(t.scale) || t.scale < .92 || t.scale > 1.08 ||
        !Number.isFinite(t.x) || !Number.isFinite(t.y)) {
      throw new Error("Invalid portrait calibration");
    }
    if (frame.color_grade !== undefined) validateColorGrade(frame.color_grade);
  });
}

export default function BustSignal({ onOpen, triggerRef }) {
  const canvasRef = useRef(null);
  const fallbackRef = useRef(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    const doc = canvasRef.current.ownerDocument, win = doc.defaultView;
    const media = win.matchMedia?.("(prefers-reduced-motion: reduce)");
    const textures = Array(ACTIVE_MANIFEST.frames.length).fill(null);
    const torsoTextures = Array(ACTIVE_MANIFEST.frames.length).fill(null);
    let cancelled = false, stopMotion, loader, interacted = false, lastYaw = -15;
    let reduced = Boolean(media?.matches), idleTimer = null, idleHandle = null, waitingLoad = false;
    const cleanup = [];

    function listen(element, type, callback, options) {
      element.addEventListener(type, callback, options);
      cleanup.push(() => element.removeEventListener(type, callback, options));
    }
    function cancelIdle() {
      if (idleTimer !== null) win.clearTimeout(idleTimer);
      if (idleHandle !== null) win.cancelIdleCallback?.(idleHandle);
      if (waitingLoad) win.removeEventListener("load", scheduleIdle);
      idleTimer = idleHandle = null;
      waitingLoad = false;
    }
    function canPrepareIdle() {
      return !cancelled && !reduced && !interacted && !doc.hidden && !win.navigator.connection?.saveData;
    }
    function scheduleIdle() {
      cancelIdle();
      if (!canPrepareIdle() || !stopMotion) return;
      if (doc.readyState !== "complete") {
        waitingLoad = true;
        win.addEventListener("load", scheduleIdle, { once: true });
        return;
      }
      // Give the initial page four seconds after load, then prepare three neighboring
      // poses during idle. The full sequence always requires real pointer/touch use.
      idleTimer = win.setTimeout(() => {
        idleTimer = null;
        if (!canPrepareIdle()) return;
        const prepare = () => {
          idleHandle = null;
          if (canPrepareIdle()) loader.loadMore({ idle: true });
        };
        if (win.requestIdleCallback) idleHandle = win.requestIdleCallback(prepare);
        else prepare();
      }, 4000);
    }
    function followLoading(clientX) {
      if (reduced || !Number.isFinite(clientX)) return;
      interacted = true;
      const center = win.innerWidth / 2;
      lastYaw = Math.max(-51, Math.min(MAX_YAW, (clientX - center) / Math.max(1, center) * 51));
      cancelIdle();
      loader.loadMore();
    }
    function followTouch(event) {
      if (event.touches?.length === 1) followLoading(event.touches[0].clientX);
    }
    try {
      validateManifest();
      loader = createPortraitLoader({
        manifest: ACTIVE_MANIFEST, assetRoot: ASSET_ROOT, restImage: fallbackRef.current, reduced,
        onTexture(index, texture) {
          if (cancelled) return;
          textures[index] = texture;
          torsoTextures[index] = prepareTorsoTexture(doc, texture, ACTIVE_MANIFEST.frames[index]);
          if (stopMotion) stopMotion.refresh();
          else {
            stopMotion = mountBustMotion(canvasRef.current, textures, ACTIVE_MANIFEST.frames, lastYaw, torsoTextures);
            setState("ready");
            scheduleIdle();
          }
        },
        onRestError() {
          if (!cancelled) { cancelIdle(); loader.destroy(); setState("error"); }
        },
      });
      listen(doc, "pointermove", event => {
        if (event.pointerType && !["mouse", "pen"].includes(event.pointerType)) return;
        followLoading(event.clientX);
      }, { passive: true });
      listen(doc, "touchstart", followTouch, { passive: true });
      listen(doc, "touchmove", followTouch, { passive: true });
      listen(doc, "visibilitychange", () => { if (doc.hidden) cancelIdle(); else scheduleIdle(); });
      if (media) listen(media, "change", () => {
        reduced = media.matches;
        loader.setReduced(reduced);
        cancelIdle();
        if (!reduced) { if (interacted) loader.loadMore(); else scheduleIdle(); }
      });
    } catch {
      setState("error");
    }
    return () => {
      cancelled = true;
      cancelIdle();
      loader?.destroy();
      stopMotion?.();
      cleanup.forEach(remove => remove());
    };
  }, []);

  return (
    <aside className="bust-signal" aria-label="Personal signal / 01">
      <div className="bust-signal-meta"><span>Personal signal / 01</span></div>
      <div className="bust-signal-visual" role="img"
        aria-label="Interactive PS2-era portrait of Nikolay Lebedinsky"
        data-bust-version="v7" data-state={state}>
        <div className="bust-signal-orbit" aria-hidden="true" />
        <div className="bust-signal-head" aria-hidden="true">
          <img className="bust-signal-fallback" ref={fallbackRef} data-bust-fallback="" alt="" decoding="async" fetchPriority="high"
            src={ASSET_ROOT + "/" + REST_FRAME.file} style={FALLBACK_STYLE} hidden={state === "ready"} />
          <canvas className="bust-signal-canvas" ref={canvasRef}
            width={manifest.width} height={manifest.height} hidden={state !== "ready"} />
        </div>
      </div>
      <a href="/about" className="bust-signal-trigger" onClick={onOpen} ref={triggerRef}>
        About me <span aria-hidden="true">↗</span>
      </a>
    </aside>
  );
}
